import { ActionContext, ActionTree } from 'vuex';

import { RootState } from '@/store';
import { State } from './state';
import { Mutations, MutationTypes } from './mutations';

import ApiService from '@/services/ApiService';

import { Status, UpsertEinstellungQRCodeTrennzeichen } from '@/models/CommonModels';

export enum ActionTypes {
  SetStatus = 'SETSTATUS',
  ToggleSidebar = 'TOGGLESIDEBAR',
  CloseSidebar = 'CLOSESIDEBAR',
  OpenSidebar = 'OPENSIDEBAR',

  GetQRCodeTrennzeichen = 'GETQRCODETRENNZEICHEN',
  SetQRCodeTrennzeichen = 'SETQRCODETRENNZEICHEN',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.SetStatus](context: ActionArguments, status: Status): void;
  [ActionTypes.ToggleSidebar](context: ActionArguments): void;
  [ActionTypes.CloseSidebar](context: ActionArguments): void;
  [ActionTypes.OpenSidebar](context: ActionArguments): void;

  [ActionTypes.GetQRCodeTrennzeichen](context: ActionArguments): Promise<void>;
  [ActionTypes.SetQRCodeTrennzeichen](context: ActionArguments, data: UpsertEinstellungQRCodeTrennzeichen): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  [ActionTypes.SetStatus]({ commit }: ActionArguments, status: Status) {
    commit(MutationTypes.SetStatus, status);
  },

  [ActionTypes.ToggleSidebar]({ commit, state }: ActionArguments) {
    const status = state.status;
    status.sidebarCollapsed = !status.sidebarCollapsed;
    commit(MutationTypes.SetStatus, status);
  },

  [ActionTypes.CloseSidebar]({ commit, state }: ActionArguments) {
    const status: Status = state.status;
    status.sidebarCollapsed = true;
    commit(MutationTypes.SetStatus, status);
  },

  [ActionTypes.OpenSidebar]({ commit, state }: ActionArguments) {
    const status: Status = state.status;
    status.sidebarCollapsed = false;
    commit(MutationTypes.SetStatus, status);
  },

  async [ActionTypes.GetQRCodeTrennzeichen]({ commit, state }: ActionArguments): Promise<void> {
    const status = state.status;
    status.getEinstellungenLoading = true;
    status.einstellungenErrorMsg = '';
    commit(MutationTypes.SetStatus, status);

    return ApiService.getEinstellungQRCodeTrennzeichen()
      .then((res) => {
        const data: string = res.data;
        commit(MutationTypes.SetQRCodeTrennzeichen, data);
      })
      .catch((error) => {
        status.einstellungenErrorMsg = error.response.data.detail;
      })
      .finally(() => {
        status.getEinstellungenLoading = false;
        commit(MutationTypes.SetStatus, status);
      });
  },

  async [ActionTypes.SetQRCodeTrennzeichen]({ commit, state }: ActionArguments, data: UpsertEinstellungQRCodeTrennzeichen): Promise<void> {
    const status = state.status;
    status.updateEinstellungenLoading = true;
    status.einstellungenErrorMsg = '';
    commit(MutationTypes.SetStatus, status);

    return ApiService.setEinstellungQRCodeTrennzeichen(data)
      .then(() => {
        commit(MutationTypes.SetQRCodeTrennzeichen, data.qrCodeTrennzeichen);
      })
      .catch((error) => {
        status.einstellungenErrorMsg = error.response.data.detail;
      })
      .finally(() => {
        status.updateEinstellungenLoading = false;
        commit(MutationTypes.SetStatus, status);
      });
  },
};
